import { getMaxUndo } from '../../../../../utils/software';

import { last, first, cloneDeep, flatMap } from 'lodash';

// ***************************************************************************************
// addNewActionForUndo() est appelée que quand on déclenche un évenement
// Permet d'ajouter une nouvelle action dans l'historique des actions en arrière (undo)
// On efface la liste des actions en avant (redo)
// ***************************************************************************************
export function addNewActionForUndo(
  actionType,
  startIndex,
  nbToUndo = 1,
  nbToRedo = 1
) {
  this.redoActions = [];

  if (this.undoActions.length >= getMaxUndo()) {
    this.undoActions.shift();
  }

  let words = this.script.filter(
    (x) => x.index >= startIndex && x.index <= startIndex + nbToUndo
  );

  const undo = {
    currentWordIndex: this.currentWordIndex,
    currentLineIndex: this.currentLineIndex,
    currentLetterIndex: this.currentLetterIndex,
    scrollOffsetLine: this.scrollOffsetLine,
    startIndex,
    nbToUndo,
    nbToRedo,
    words: cloneDeep(words),
    actionType,
  };

  this.undoActions.push(undo);
}

// ***************************************************************************************
// addActionForUndoOrRedo() est appelée quand on fait undo or redo
// Permet d'ajouter une nouvelle action dans l'historique des actions en avant (redo) ou en arrière (undo)
// ***************************************************************************************
export function addActionForUndoOrRedo(oldAction, words, redo = false) {
  const action = {
    ...oldAction,
    currentWordIndex: this.currentWordIndex,
    currentLineIndex: this.currentLineIndex,
    currentLetterIndex: this.currentLetterIndex,
    scrollOffsetLine: this.scrollOffsetLine,
    words: cloneDeep(words),
  };

  if (redo) {
    this.redoActions.shift();
    this.undoActions.push(action);
  } else {
    this.redoActions.unshift(action);
    this.undoActions.pop();
  }

  // si l'undo contient un mot vide
  if (oldAction.words.length === 1 && first(oldAction.words).text === '') {
    this.hasEmptyWord = true;
  }
}

// ***************************************************************************************
// onUndoOrRedo permet de revenir en arrière ou d'annuler le retour en arrière
// ***************************************************************************************
export function onUndoOrRedo(key) {
  let action =
    key === 'UNDO' ? last(this.undoActions) : first(this.redoActions);

  if (!action) {
    return;
  }

  if (action.actionType === 'onReplaceAll') {
    this.onReplaceAllWhenUndoOrRedo(key);
    return;
  }

  if (action.underSelection) {
    this.onUnderSelectionWhenUndoOrRedo(key);
    return;
  }

  const nb = key === 'UNDO' ? action.nbToRedo : action.nbToUndo;

  let history = this.script.filter(
    (x) => x.index >= action.startIndex && x.index <= action.startIndex + nb
  );

  if (key === 'UNDO') {
    // Si submitted, alors je supprime tous les undo
    if (history.some((x) => x.submitted)) {
      this.undoActions = [];
      return;
    }
  }

  this.script.splice(action.startIndex, history.length, ...action.words);

  if (key === 'UNDO') {
    this.addActionForUndoOrRedo(action, history);
  } else {
    this.addActionForUndoOrRedo(action, history, true);
  }

  this.onRedrawAfterRedoOrUndo(action);
}

// ***************************************************************************************
// onReplaceAllWhenUndoOrRedo permet de remplacer tous les mots en cas d'undo ou de redo
// ***************************************************************************************
export function onReplaceAllWhenUndoOrRedo(key) {
  let action =
    key === 'UNDO' ? last(this.undoActions) : first(this.redoActions);

  const indexs = action.words.map((w) => w.index);
  const wordsToReplace = this.script.filter(
    (x) => !x.submitted && indexs.includes(x.index)
  );

  if (key === 'UNDO') {
    const redo = {
      actionType: 'onReplaceAll',
      oldText: action.newText,
      newText: action.oldText,
      words: wordsToReplace,
    };

    this.redoActions.unshift(redo);
    this.undoActions.pop();
  } else {
    const undo = {
      actionType: 'onReplaceAll',
      oldText: action.newText,
      newText: action.oldText,
      words: wordsToReplace,
    };

    this.undoActions.push(undo);

    this.redoActions.shift();
  }

  wordsToReplace.forEach((wordToReplace) => {
    wordToReplace.text = wordToReplace.text.replace(
      action.oldText,
      action.newText
    );

    this.onChange(wordToReplace.startTime, wordToReplace.index, false);
  });

  this.onSearchChange(this.state.search.searchText);
}

// ***************************************************************************************
// addNewActionOnUnderSelectionForUndo
// permet de créer une nouvelle action quand on est en mode sélection
// ***************************************************************************************
export function addNewActionOnUnderSelectionForUndo(
  actionType,
  index,
  words,
  callback = null,
  oldWords = null
) {
  const undo = {
    actionType: actionType,
    underSelection: true,
    wordsToSelect: cloneDeep(this.wordsToSelect),
    lettersToSelect: cloneDeep(this.lettersToSelect),
    words: cloneDeep(words),
    oldWords: oldWords ? cloneDeep(oldWords) : null,
    startIndex: index,
    currentWordIndex: this.currentWordIndex,
    currentLineIndex: this.currentLineIndex,
    currentLetterIndex: this.currentLetterIndex,
    scrollOffsetLine: this.scrollOffsetLine,
    callback,
  };

  this.undoActions.push(undo);
}

// ***************************************************************************************
// onUnderSelectionWhenUndoOrRedo permet de faire undo ou redo en cas de sélection
// ***************************************************************************************
export function onUnderSelectionWhenUndoOrRedo(key) {
  if (key === 'UNDO') {
    const action = last(this.undoActions);

    // on supprime les mots ajoutés précédemment
    if (action.actionType === 'addChar') {
      this.script.splice(action.startIndex, 1);
    }

    if (action.actionType === 'onSetColor') {
      this.script.splice(action.startIndex, action.words.length);
    }

    if (action.actionType === 'onPaste') {
      this.script.splice(action.startIndex, action.oldWords.length);
    }

    let nbToDelete = 0;
    if (action.actionType !== 'addChar' && action.lettersToSelect.length) {
      const letters = flatMap(action.words.map((x) => x.letters));

      if (letters.length !== action.lettersToSelect.length) {
        nbToDelete = 1;
      }
    }

    this.script.splice(action.startIndex, nbToDelete, ...action.words);

    this.redoActions.unshift(action);
    this.undoActions.pop();

    this.underSelection = true;
    this.wordsToSelect = action.wordsToSelect;
    this.lettersToSelect = action.lettersToSelect;

    this.onRedrawAfterRedoOrUndo(action);
  } else {
    const action = first(this.redoActions);

    this.redoActions.shift();

    this.underSelection = true;

    if (action.wordsToSelect.length) {
      this.wordsToSelect = action.wordsToSelect;
    }

    if (action.lettersToSelect.length) {
      this.lettersToSelect = action.lettersToSelect;
    }

    if (action.callback) {
      this.forceRecalculateLines = true;
      action.callback();
    }
  }
}

// ***************************************************************************************
// onRedrawAfterRedoOrUndo permet de redessiner après avoir faire redo ou undo
// ***************************************************************************************
export function onRedrawAfterRedoOrUndo(action) {
  // Si on fait undo ou redo, on revérifie l'orthographe de la phrase entière
  const newWord = this.script[action.startIndex];

  // Doit-on activer le correcteur automatique à chaque fois ?
  /* if (newWord) {
    this.onCheckLineByWord(newWord);
  }*/

  this.currentWordIndex = action.currentWordIndex;
  this.currentLineIndex = action.currentLineIndex;
  this.currentLetterIndex = action.currentLetterIndex;

  if (!this.script.length) {
    this.onChange(0, -1);
    return;
  }

  this.forceRecalculateLines = true;

  this.onChange(newWord.startTime, newWord.index);

  if (this.scrollOffsetLine !== action.scrollOffsetLine) {
    this.scrollOffsetLine = action.scrollOffsetLine;
    this.changeScrollValueState(this.scrollOffsetLine);
  }

  this.onMove();
}

// ***************************************************************************************
// addScriptToNewUndo permet de stocker tout le script dans undo
// ***************************************************************************************
export function addScriptToNewUndo(actionType) {
  this.addNewActionForUndo(
    actionType,
    0,
    this.script.length,
    this.script.length
  );
}
